/**
 * Evolution scale helpers — tune by geographic leaf size, not org slug.
 *
 * Every run request is aggregated over the leaves of the selected scope, so a
 * large scope makes the full run fan-out slow. The run set stays the same for
 * every org; only the paint strategy changes.
 */

/** Above this leaf count a single run request is slow enough to paint early. */
export const EVOLUTION_MEDIUM_LEAF_THRESHOLD = 50;

type TreeNodeLike = {
  id?: string;
  trees?: TreeNodeLike[] | null;
};

/** Count leaf nodes under a tree node (node with no children = 1 leaf). */
export function countLeafNodes(node: TreeNodeLike | null | undefined): number {
  if (!node) return 0;
  const children = node.trees ?? [];
  if (children.length === 0) return 1;
  return children.reduce((sum, child) => sum + countLeafNodes(child), 0);
}

/**
 * Effective leaf scope for the SIP selection.
 * Virtual org-root counts leaves under all of its children (not 1).
 * Unknown / empty → 0, which keeps the default (non-progressive) behavior.
 */
export function countScopeLeaves(
  node: TreeNodeLike | null | undefined,
): number {
  if (!node) return 0;
  if (node.id === "org-root") {
    const children = node.trees ?? [];
    if (children.length === 0) return 0;
    return children.reduce((sum, child) => sum + countLeafNodes(child), 0);
  }
  return countLeafNodes(node);
}

/** Paint partial results while remaining runs load (medium/large leaf scope). */
export function shouldProgressiveEvolution(scopeLeafCount: number): boolean {
  return scopeLeafCount > EVOLUTION_MEDIUM_LEAF_THRESHOLD;
}
